import { ProductGroups } from 'enums';
import useAppSync from 'hooks/use-app-sync';
import React, { useEffect, useRef } from 'react';
import AccountAlreadyExistsDialog from './account-already-exists-dialog';
import AccountAlreadySyncedDialog from './account-already-synced-dialog';
import AppSyncDialog from './app-sync-dialog/app-sync-dialog';
import { LinkedinAccount } from './linkedin-accounts.types';
import WaitBeforeSyncDialog from './wait-before-sync-dialog';

/** What the host should open: a new account, or a fresh login for an existing one. */
export type LinkedinConnectRequest = { mode: 'new' } | { mode: 'resync'; account: LinkedinAccount };

interface Props {
  /** null = closed. */
  request: LinkedinConnectRequest | null;
  productGroup: ProductGroups;
  onClose(): void;
  /** The success step's main button ("weiter"). */
  onConnected?(): void;
}

/**
 * Der Verbinden-Dialog der Kontoverwaltung (AppSyncDialog) als eigenständiger Baustein:
 * feste IP, Gerät, Anmeldung, 2FA, Captcha, Vertragswahl — derselbe Ablauf, egal ob aus der
 * Kontoverwaltung oder aus dem Einrichtungs-Wizard geöffnet. Ein Dialog, zwei Aufrufwege;
 * der Wizard hatte vorher ein eigenes Formular, das an der normalen Kontoanlage vorbeiging.
 */
const LinkedinConnectDialogHost: React.FC<Props> = ({ request, productGroup, onClose, onConnected }) => {
  const {
    appSyncDialog,
    allCountryCodes,
    handleOpenAppSyncDialog,
    handleOpenAppSyncAndSynchronizeLiAccount,
    handleCloseAppSyncDialog,
    increaseActiveStepIndex,
    decreaseActiveStepIndex,
    handleAppSyncLinkedinProductChange,
    handleAppSyncCountryChange,
    handleAppSyncCityChange,
    handleAppSyncUserNameChange,
    handleAppSyncPasswordChange,
    handleAppSyncLicenseSelect,
    handleCreateAndSynchronizeLiAccount,
    handleAppSyncVerificationCodeChange,
    handleVerifyLinkedinAccountFromAppSync,
    handleImageClickFromAppSync,
    handleDeleteMarkerFromAppSync,
    handleSolveCaptchaFromAppSync,
    handleSelectContractFromAppSync,
    handleAddContractFromAppSync,
    handleEditAndSynchronizeLiAccount,
    handleReSynchronizeLiAccount,
    handleResetLinkedinAccount,
    handleThirdPartyClickFromAppSync,
    handleThirdPartyTextChange,
    handleSetThirdPartyTextFromAppSync,
    accountAlreadyExistsDialog,
    handleCloseAccountAlreadyExistsDialog,
    waitBeforeSyncDialog,
    handleCloseWaitBeforeSyncDialog,
    accountAlreadySyncedDialog,
    handleCloseAccountAlreadySyncedDialog,
  } = useAppSync(true);

  // Each new request opens the dialog once — the hook keeps its own state from there on.
  const openedFor = useRef<LinkedinConnectRequest | null>(null);
  useEffect(() => {
    if (!request || openedFor.current === request) return;
    openedFor.current = request;
    if (request.mode === 'resync') handleOpenAppSyncAndSynchronizeLiAccount(request.account)();
    else handleOpenAppSyncDialog(false)();
  }, [request]);

  const close = () => {
    handleCloseAppSyncDialog();
    openedFor.current = null;
    onClose();
  };

  return (
    <>
      <AppSyncDialog
        open={!!request && appSyncDialog.open}
        product={productGroup}
        handleClose={close}
        isStarterExperience={appSyncDialog.isStarterExperience}
        isLogoutMode={appSyncDialog.isLogoutMode}
        activeStepIndex={appSyncDialog.activeStepIndex}
        loading={appSyncDialog.loading}
        syncProcessingStatus={appSyncDialog.syncProcessingStatus}
        syncError={appSyncDialog.error}
        selectedLinkedinProduct={appSyncDialog.selectedLinkedinProduct}
        allCountryCodes={allCountryCodes}
        selectedCountry={appSyncDialog.selectedCountry}
        selectedCity={appSyncDialog.selectedCity}
        handleLinkedinProductChange={handleAppSyncLinkedinProductChange}
        handleCountryChange={handleAppSyncCountryChange}
        handleCityChange={handleAppSyncCityChange}
        userName={appSyncDialog.userName}
        password={appSyncDialog.password}
        linkedinAccount={appSyncDialog.linkedinAccount}
        verificationCode={appSyncDialog.verificationCode}
        captcha={appSyncDialog.captcha}
        markerPosition={appSyncDialog.markerPosition}
        thirdPartyScreenshot={appSyncDialog.thirdPartyScreenshot}
        thirdPartyText={appSyncDialog.thirdPartyText}
        linkedinContracts={appSyncDialog.foundContractNames}
        selectedContract={appSyncDialog.selectedContract}
        increaseActiveStepIndex={increaseActiveStepIndex}
        decreaseActiveStepIndex={decreaseActiveStepIndex}
        handleUserNameChange={handleAppSyncUserNameChange}
        handlePasswordChange={handleAppSyncPasswordChange}
        moveToCampaignBuilder={() => {
          close();
          onConnected?.();
        }}
        handleCreateAndSynchronizeLiAccount={handleCreateAndSynchronizeLiAccount}
        handleVerificationCodeChange={handleAppSyncVerificationCodeChange}
        handleVerifyLinkedinAccountFromAppSync={handleVerifyLinkedinAccountFromAppSync}
        handleImageClick={handleImageClickFromAppSync}
        handleDeleteMarker={handleDeleteMarkerFromAppSync}
        handleSolveCaptcha={handleSolveCaptchaFromAppSync}
        handleThirdPartyClick={handleThirdPartyClickFromAppSync}
        handleThirdPartyTextChange={handleThirdPartyTextChange}
        handleSetThirdPartyTextFromAppSync={handleSetThirdPartyTextFromAppSync}
        handleSelectContract={handleSelectContractFromAppSync}
        handleAddContractFromAppSync={handleAddContractFromAppSync}
        handleEditAndSynchronizeLiAccount={handleEditAndSynchronizeLiAccount}
        handleReSynchronizeLiAccount={handleReSynchronizeLiAccount}
        onResetAccount={handleResetLinkedinAccount}
        selectedLicenseId={appSyncDialog.selectedLicenseId}
        handleLicenseSelect={handleAppSyncLicenseSelect}
      />
      <AccountAlreadyExistsDialog
        open={accountAlreadyExistsDialog.open}
        userEmail={accountAlreadyExistsDialog.userEmail}
        handleClose={handleCloseAccountAlreadyExistsDialog}
      />
      <WaitBeforeSyncDialog
        open={waitBeforeSyncDialog.open}
        waitingTime={waitBeforeSyncDialog.waitingTime}
        handleClose={handleCloseWaitBeforeSyncDialog}
      />
      <AccountAlreadySyncedDialog open={accountAlreadySyncedDialog.open} handleClose={handleCloseAccountAlreadySyncedDialog} />
    </>
  );
};

export default LinkedinConnectDialogHost;
